import { useTranslation } from 'react-i18next';

import { CelebrationType } from '../../types';

export const useCelebrationDisplay = (
	celebrationType: CelebrationType,
	celebrationCreatedAt?: string, // this year's event date (undefined for upcoming events)
	celebrationDate?: string // original event date (for anniversary)
) => {
	const { t } = useTranslation('celebrations');

	let emoji = '';
	let text = '';

	// NOTE: for celebrations that happen on a weekend, we want to display the actual celebration date
	// the created at date can be a day or two before the actual celebration date because celebrations
	// are created on weekdays and the actual celebration date might be on a weekend
	const date = celebrationDate || celebrationCreatedAt || '';

	// NOTE: celebrationDate is formatted like this `YYYY-MM-DD` and celebrationCreatedAt is `YYYY-MM-DDTHH:MM:SSZ`
	// so we need to make them all formatted the same with slashes like this `YYYY/MM/DD` otherwise its off by a day for celebrationDate
	const slashDate = new Date(date.replace(/-/g, '/').replace(/T.+/, ''));
	const displayDate = slashDate.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
	});

	// NOTE: date might not have today's year so we need to look at month and day only
	const isToday = slashDate.getMonth() === new Date().getMonth() && slashDate.getDate() === new Date().getDate();

	// NOTE: when it is December, for upcoming anniversaries in January
	// we need to compare to next year to get the accurate anniversary number
	const todayIsDec = new Date().getMonth() === 11;
	const celebrationIsInJan = celebrationDate ? new Date(celebrationDate).getMonth() === 0 : false;
	let comparisonYear = new Date().getFullYear();
	if (todayIsDec && celebrationIsInJan) {
		comparisonYear++;
	}

	// NOTE: celebration date is formatted "YYYY=MM-DD"
	// When converting to a date object, it is sometimes the year before for Jan 1
	// so lets just grab the year out of th date string instead of converting to a date object
	const anniversaryYear = celebrationDate ? parseInt(celebrationDate.split('-')[0]) : 0;
	const anniversaryNumber = celebrationType === CelebrationType.Anniversary && celebrationDate ? comparisonYear - anniversaryYear : 0;

	// NOTE: need to know if welcome is in the future for different text
	const isFuture = slashDate > new Date();

	// NOTE: set emoji based on celebration type
	switch (celebrationType) {
		case CelebrationType.Anniversary:
			emoji = '🎉';
			break;
		case CelebrationType.Birthday:
			emoji = '🎈';
			break;
		case CelebrationType.Welcome:
			emoji = '👋';
			break;
		default:
			break;
	}

	// NOTE: set celebration message based on celebration type and if its today or not
	switch (true) {
		case celebrationType === CelebrationType.Anniversary && isToday:
			text = `${t('widget.anniversary_today', { anniversaryNumber })}`;
			break;
		case celebrationType === CelebrationType.Anniversary && !isToday:
			text = `${t('widget.anniversary_date', { anniversaryNumber, date: displayDate })}`;
			break;
		case celebrationType === CelebrationType.Birthday && isToday:
			text = `${t('widget.birthday_today')}`;
			break;
		case celebrationType === CelebrationType.Birthday && !isToday:
			text = `${t('widget.birthday_date', { date: displayDate })}`;
			break;
		case celebrationType === CelebrationType.Welcome && isFuture:
			text = `${t('widget.welcome_future', { date: displayDate })}`;
			break;
		case celebrationType === CelebrationType.Welcome && isToday:
			text = `${t('widget.welcome_today')}`;
			break;
		case celebrationType === CelebrationType.Welcome && !isToday:
			text = `${t('widget.welcome_date', { date: displayDate })}`;
			break;
		default:
			break;
	}

	return {
		emoji,
		text,
	};
};
